
import React, { createContext, useContext } from 'react';
import { useDashboardState } from './useDashboardState';
import { KpiCardType, KpiItem } from './types';

export interface DashboardContextType {
  kpiCards: KpiCardType[];
  isEditMode: boolean;
  toggleEditMode: () => void;
  updateKpiCard: (id: string, updates: Partial<KpiCardType>) => void;
  removeKpiCard: (id: string) => void;
  addKpiCard: (card: Partial<KpiCardType>) => void;
  moveKpiCard: (id: string, direction: 'left' | 'right' | 'up' | 'down') => void;
  
  // Properties left for backward compatibility but won't be used for drag and drop
  dragStart: (id: string) => void;
  dragOver: (id: string) => void;
  drop: () => void;
  draggedCardId: string | null;
  dragOverCardId: string | null;
  
  // Adding backward compatibility for older components
  kpis: KpiItem[];
  department: string;
  updateKpiOrder: (items: KpiItem[]) => void;
  toggleKpiVisibility: (id: string) => void;
  resetToDefault: (department?: string) => void;
  initializeKpis: (items: KpiItem[]) => void;
}

const DashboardContext = createContext<DashboardContextType | undefined>(undefined);

export const DashboardProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const dashboardState = useDashboardState();
  
  // Create backward compatibility layer
  const compatibilityLayer = {
    ...dashboardState,
    
    // Convert kpiCards to kpis format for backward compatibility
    kpis: dashboardState.kpiCards.map(card => ({
      id: card.id,
      title: card.title,
      department: 'dashboard',
      isVisible: card.visible,
      order: card.order,
      component: card.component
    })),
    
    department: 'dashboard',
    
    // Map new functions to match old interface
    updateKpiOrder: (items: KpiItem[]) => {
      // This would need a proper implementation but for now we'll provide a basic one
      console.log('updateKpiOrder called with:', items);
    },
    
    toggleKpiVisibility: (id: string) => {
      const card = dashboardState.kpiCards.find(c => c.id === id);
      if (card) {
        dashboardState.updateKpiCard(id, { visible: !card.visible });
      }
    },
    
    resetToDefault: (department?: string) => {
      console.log('resetToDefault called for department:', department || 'dashboard');
      // Here you would reset to default cards
    },
    
    initializeKpis: (items: KpiItem[]) => {
      console.log('initializeKpis called with:', items);
      // This would initialize the KPIs from the given items
    }
  };
  
  return (
    <DashboardContext.Provider value={compatibilityLayer}>
      {children}
    </DashboardContext.Provider>
  );
};

export const useDashboard = (): DashboardContextType => {
  const context = useContext(DashboardContext);
  if (context === undefined) {
    throw new Error('useDashboard must be used within a DashboardProvider');
  }
  return context;
};
